import React, { useState, useRef, useEffect } from 'react';
import '../../../../styles/profile/components/month-picker.css';

interface MonthPickerProps {
  selectedDate: Date | null;
  onSelect: (date: Date) => void;
  onClose: () => void;
  minDate?: Date;
  maxDate?: Date;
}

export default function MonthPicker({ selectedDate, onSelect, onClose, minDate, maxDate }: MonthPickerProps) {
  const [currentYear, setCurrentYear] = useState(
    selectedDate ? selectedDate.getFullYear() : new Date().getFullYear()
  );
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handlePrevYear = () => {
    setCurrentYear(currentYear - 1);
  };

  const handleNextYear = () => {
    setCurrentYear(currentYear + 1);
  };

  const handleMonthClick = (month: number) => {
    const newDate = new Date(currentYear, month, 1);
    
    if (minDate && newDate < new Date(minDate.getFullYear(), minDate.getMonth(), 1)) {
      return;
    }
    if (maxDate && newDate > new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)) {
      return;
    }
    
    onSelect(newDate);
  };

  const isMonthSelected = (month: number): boolean => {
    if (!selectedDate) return false;
    return selectedDate.getFullYear() === currentYear && selectedDate.getMonth() === month;
  };

  const isMonthDisabled = (month: number): boolean => {
    const date = new Date(currentYear, month, 1);
    
    if (minDate && date < new Date(minDate.getFullYear(), minDate.getMonth(), 1)) {
      return true;
    }
    if (maxDate && date > new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)) {
      return true;
    }
    
    return false;
  };

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <div className="month-picker" ref={pickerRef}>
      <div className="month-picker-header">
        <button className="month-picker-nav" onClick={handlePrevYear} type="button">
          ‹
        </button>
        <span className="month-picker-year">{currentYear}</span>
        <button className="month-picker-nav" onClick={handleNextYear} type="button">
          ›
        </button>
      </div>

      <div className="month-picker-months">
        {monthNames.map((monthName, index) => (
          <button
            key={index}
            type="button"
            className={`month-picker-month ${isMonthSelected(index) ? 'selected' : ''} ${isMonthDisabled(index) ? 'disabled' : ''}`}
            onClick={() => handleMonthClick(index)}
            disabled={isMonthDisabled(index)}
          >
            {monthName}
          </button>
        ))}
      </div>
    </div>
  );
}

